import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import * as React from 'react';
import React__default, { useContext } from 'react';
import classnames from 'classnames';
import { Tooltip } from '@grandvisionhq/common';
import { useIntl } from '@grandvisionhq/state';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function getAvailableQuantity(availability) {
    var channels = (availability && availability.channels) || {};
    var quantity = Object.entries(channels)
        .map(function (_a) {
        var _b = __read(_a, 2); _b[0]; var channel = _b[1];
        return (channel && channel.availableQuantity) || 0;
    })
        .reduce(function (acc, val) { return acc + val; }, 0);
    return quantity;
}

var helpers$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    getAvailableQuantity: getAvailableQuantity
});

var defaultStockLevels = [
    // Order matters! The `findStock()` method will walk through this array and stop at its first match
    {
        threshold: 10,
        labelId: 'inStock',
    },
    {
        threshold: 5,
        labelId: 'lowStock',
    },
    {
        threshold: 1,
        labelId: 'veryLowStock',
    },
    {
        threshold: 0,
        labelId: 'outOfStock',
    },
];
var getStockLevels = function (thresholdConfig) {
    if (thresholdConfig === void 0) { thresholdConfig = []; }
    return defaultStockLevels.map(function (level) { return (__assign(__assign({}, level), thresholdConfig.find(function (threshold) { return threshold.labelId === level.labelId; }))); });
};
var defaultStockResolver = function (allowBackOrder, stockCount, thresholdConfig, productTypeName) {
    // opcos can display a stock message on frames PDP by using a custom stock resolver
    if (typeof stockCount !== 'number' ||
        (productTypeName && CommerceToolsApi.isFrames(productTypeName))) {
        return;
    }
    var positiveStockCount = Math.max(stockCount, 0); // No negative stockCount
    if (!positiveStockCount && allowBackOrder) {
        return {
            threshold: 0,
            labelId: 'backOrder',
        };
    }
    return getStockLevels(thresholdConfig).find(function (stockLevel) { return positiveStockCount >= stockLevel.threshold; });
};

var helpers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    defaultStockLevels: defaultStockLevels,
    getStockLevels: getStockLevels,
    defaultStockResolver: defaultStockResolver
});

var settingsDefaults = {
    thresholds: [],
};
var StockIndicatorContext = React__default.createContext({});
var useStockIndicator = function () { return useContext(StockIndicatorContext); };

var inventoryClassNames = {
    inStock: 'in-stock',
    lowStock: 'low-stock',
    veryLowStock: 'very-low-stock',
    outOfStock: 'out-of-stock',
    backOrder: 'back-order',
    availableInStore: 'available-in-store',
};
var StockIndicator = function (_a) {
    var _b;
    var stockCount = _a.stockCount, _c = _a.allowBackOrder, allowBackOrder = _c === void 0 ? true : _c, stockResolver = _a.stockResolver, productTypeName = _a.productTypeName;
    var getLabel = useIntl().getLabel;
    var thresholds = useStockIndicator().thresholds;
    var stock = stockResolver
        ? stockResolver(allowBackOrder, stockCount, thresholds, productTypeName)
        : defaultStockResolver(allowBackOrder, stockCount, thresholds, productTypeName);
    if (!stock) {
        return null;
    }
    var labelId = stock.labelId, tooltipId = stock.tooltipId;
    var shouldShowTooltip = tooltipId || labelId === 'backOrder';
    var inventoryClass = labelId.substr(labelId.lastIndexOf('.') + 1);
    return (React.createElement("div", { className: classnames('stock-indicator', (_b = {},
            _b["stock-indicator--level-" + inventoryClassNames[inventoryClass]] = inventoryClassNames[inventoryClass],
            _b)) },
        React.createElement("div", { className: "stock-indicator__levels" },
            React.createElement("span", { className: "stock-indicator__levels-level" })),
        React.createElement("span", { className: "stock-indicator__label" }, getLabel("products.stockInfo." + labelId)),
        shouldShowTooltip && (React.createElement(Tooltip, { id: "stock-indicator", className: "stock-indicator__tooltip", 
            // All stock levels can have a tooltip when configured through a custom config
            // inStockTooltip, lowStockTooltip, veryLowStockTooltip, outOfStockTooltip, backOrderTooltip
            message: getLabel("products.stockInfo." + (tooltipId || 'backOrder') + "Tooltip"), placement: "left" }))));
};

var createContext = function (settings) {
    if (settings === void 0) { settings = {}; }
    return __assign(__assign({}, settingsDefaults), settings);
};
var StockIndicatorProvider = function (_a) {
    var children = _a.children, settings = _a.settings;
    return (React.createElement(StockIndicatorContext.Provider, { value: createContext(settings) }, children));
};

export { helpers$1 as Helpers, StockIndicator, StockIndicatorContext, helpers as StockIndicatorHelpers, StockIndicatorProvider };
//# sourceMappingURL=index.js.map
